import { useState, useRef } from "react";
import { api } from "../lib/apiClient";
import { Upload, FileText, Loader2, AlertTriangle, CheckCircle2, Scale, Clock, DollarSign, Hash, Building2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const DOC_TYPE_FR = {
  amende: "Amende / PV",
  avis_prefecture: "Avis Préfecture",
  prefecture: "Préfecture",
  convocation_tribunal: "Convocation Tribunal",
  tribunal: "Tribunal",
  bail: "Bail / Logement",
  contrat_travail: "Contrat de travail",
  employeur: "Notification employeur",
  mise_en_demeure: "Mise en demeure",
  avis_caf: "Avis CAF",
  caf: "CAF",
  poleemploi: "Pôle Emploi",
  facture: "Facture",
  ordonnance: "Ordonnance",
  plainte: "Plainte",
  huissier: "Huissier",
  banque: "Banque",
  autre: "Autre",
};

const CONFIDENCE_FR = { high: "Fiabilité élevée", medium: "Fiabilité moyenne", low: "Fiabilité faible" };

const ACTION_STYLES = {
  urgent: { label: "URGENT", cls: "bg-red-100 text-red-800 border-red-300", icon: <AlertTriangle className="w-3 h-3" /> },
  self: { label: "Vous pouvez agir seul(e)", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: <CheckCircle2 className="w-3 h-3" /> },
  lawyer: { label: "Avocat recommandé", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: <Scale className="w-3 h-3" /> },
  no_action: { label: "Informatif", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: <FileText className="w-3 h-3" /> },
  unclear: { label: "À évaluer", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: <FileText className="w-3 h-3" /> },
};

const LITIG_LABEL = {
  high: { label: "Litige probable", cls: "text-red-700 font-bold" },
  medium: { label: "Litige possible", cls: "text-amber-700 font-bold" },
  low: { label: "Peu de litige", cls: "text-slate-700 font-semibold" },
  unclear: { label: "Indéterminé", cls: "text-slate-700 font-semibold" },
};

/**
 * Scanne un document administratif (PDF/image), extrait signaux structurés
 * (dates, montants, autorité, délai, litige), et propose action self vs lawyer.
 */
export function DocumentScanner({ onExtractText }) {
  const [loading, setLoading] = useState(false);
  const [signals, setSignals] = useState(null);
  const [error, setError] = useState("");
  const [filename, setFilename] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setSignals(null);
    setFilename(file.name);
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/documents/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setSignals(r.data);
      // Feed extracted context to parent intake — APPEND, never overwrite existing description
      if (onExtractText) {
        const parts = [];
        if (r.data.document_type) parts.push(`Type: ${DOC_TYPE_FR[r.data.document_type] || r.data.document_type}`);
        if (r.data.issuing_authority) parts.push(`Émis par: ${r.data.issuing_authority}`);
        if (r.data.dates?.length) parts.push(`Dates: ${r.data.dates.join(", ")}`);
        if (r.data.amounts?.length) parts.push(`Montants: ${r.data.amounts.join(", ")}`);
        if (r.data.references?.length) parts.push(`Réf: ${r.data.references.join(", ")}`);
        if (r.data.deadline_days) parts.push(`Délai: ${r.data.deadline_days} jours`);
        if (r.data.action_summary) parts.push(`Action: ${r.data.action_summary}`);
        if (r.data.key_points?.length) parts.push(`Points: ${r.data.key_points.join(" · ")}`);
        onExtractText(parts.join("\n"));
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401 || status === 403) {
        setError("Connectez-vous d'abord pour scanner un document (protection quota).");
        setTimeout(() => { window.location.href = "/login"; }, 1500);
      } else if (status === 429) {
        setError(detail || "Limite atteinte. Réessayez dans 10 minutes.");
      } else if (status === 503) {
        setError(detail || "Service d'analyse saturé. Réessayez dans quelques minutes.");
      } else if (status === 415) {
        setError(detail || "Format non supporté. Utilisez PDF, JPEG, PNG, WEBP, HEIC ou DOCX.");
      } else {
        // Guard: keep error short & French even if server leaks something long
        const safe = typeof detail === "string" && detail.length < 200 ? detail : "Analyse échouée. Réessayez avec un document lisible.";
        setError(safe);
      }
    } finally {
      setLoading(false);
    }
  };

  const action = signals ? (ACTION_STYLES[signals.recommended_action] || ACTION_STYLES.unclear) : null;
  const litig = signals ? (LITIG_LABEL[signals.litigation_potential] || LITIG_LABEL.unclear) : null;

  return (
    <div className="j-card border-2 border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50/40 to-transparent" data-testid="doc-scanner">
      <div className="flex items-center gap-2 mb-1">
        <Upload className="w-5 h-5 text-emerald-600" />
        <span className="text-xs uppercase tracking-widest font-bold text-emerald-700">Scanner un document</span>
      </div>
      <h3 className="font-heading font-black text-lg text-[#1E293B]">Bail locatif · État des lieux · Courrier de retenue · Décompte de charges</h3>
      <p className="text-sm text-slate-600 mt-1">Photo, PDF, Word ou HEIC iPhone — extraction factuelle des dates, montants, parties et délais.</p>
      <p className="text-[0.7rem] text-slate-500 mt-1">🔒 Compte requis — protège votre quota d'analyse et sécurise vos documents.</p>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.heic,.heif,image/*,image/heic,image/heif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={handleFile}
        data-testid="doc-scanner-input"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={loading}
        className="btn-emerald w-full mt-4"
        data-testid="doc-scanner-btn"
      >
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyse en cours…</> : <><Upload className="w-5 h-5" /> Choisir un document</>}
      </button>

      {filename && !loading && <div className="text-xs text-slate-500 mt-2 truncate" data-testid="doc-scanner-filename">📎 {filename}</div>}

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2" data-testid="doc-scanner-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {signals && (
        <div className="mt-5 space-y-4" data-testid="doc-scanner-results">
          {/* Action recommandée + badge litige */}
          <div className="flex items-start gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black border ${action.cls}`} data-testid="doc-scanner-action">
              {action.icon} {action.label}
            </span>
            <span className={`text-xs ${litig.cls}`} data-testid="doc-scanner-litig">{litig.label}</span>
            <span className="text-[0.65rem] text-slate-600 ml-auto font-semibold">{CONFIDENCE_FR[signals.confidence] || signals.confidence}</span>
          </div>

          {signals.action_summary && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Action prioritaire</div>
              <div className="text-sm text-slate-800 font-semibold" data-testid="doc-scanner-summary">{signals.action_summary}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs">
            {signals.document_type && <Meta icon={<FileText className="w-3 h-3" />} label="Type" value={DOC_TYPE_FR[signals.document_type] || signals.document_type} />}
            {signals.issuing_authority && <Meta icon={<Building2 className="w-3 h-3" />} label="Autorité" value={signals.issuing_authority} />}
            {signals.dates?.length > 0 && <Meta icon={<Clock className="w-3 h-3" />} label="Dates" value={signals.dates.slice(0, 3).join(" · ")} />}
            {signals.amounts?.length > 0 && <Meta icon={<DollarSign className="w-3 h-3" />} label="Montants" value={signals.amounts.slice(0, 3).join(" · ")} />}
            {signals.references?.length > 0 && <Meta icon={<Hash className="w-3 h-3" />} label="Références" value={signals.references.slice(0, 2).join(" · ")} />}
            {signals.deadline_days != null && <Meta icon={<Clock className="w-3 h-3" />} label="Délai" value={`${signals.deadline_days} jours`} />}
          </div>

          {signals.key_points?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Points clés</div>
              <ul className="text-xs text-slate-700 space-y-1">
                {signals.key_points.map((p, i) => <li key={i} className="flex gap-1.5"><span className="text-emerald-500">•</span> {p}</li>)}
              </ul>
            </div>
          )}

          {signals.missing_or_uncertain?.length > 0 && (
            <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="doc-scanner-uncertain">
              ⚠️ À vérifier : {signals.missing_or_uncertain.join(", ")}
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-slate-200 flex-wrap">
            <button
              onClick={() => window.scrollTo({ top: 500, behavior: "smooth" })}
              className="btn-emerald flex-1 text-sm"
              data-testid="doc-scanner-cta-self"
            >
              <ArrowRight className="w-4 h-4" /> Compléter la pré-instruction
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ icon, label, value }) {
  return (
    <div className="p-2 rounded-lg bg-white border border-slate-200">
      <div className="flex items-center gap-1 text-[0.6rem] uppercase tracking-widest text-slate-500 font-bold">{icon} {label}</div>
      <div className="text-slate-800 font-semibold mt-0.5 break-words">{value}</div>
    </div>
  );
}
